import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * Formats a YYYY-MM-DD date string into ordinal format: "29th Apr 2026"
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
function formatDateOrdinal(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  if (isNaN(date.getTime())) return dateStr;

  const day = date.getDate();
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  const suffix = (d) => {
    if (d >= 11 && d <= 13) return "th";
    switch (d % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  return `${day}${suffix(day)} ${month} ${year}`;
}

/**
 * Parses an ordinal-formatted date string ("29th Apr 2026") back into YYYY-MM-DD.
 * Returns the original string if it's already in ISO format or can't be parsed.
 * @param {string} dateStr - Date in ordinal format or YYYY-MM-DD
 * @returns {string} Date in YYYY-MM-DD format
 */
function parseOrdinalDate(dateStr) {
  if (!dateStr) return "";

  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) return dateStr.trim();

  const months = {
    Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
    Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
  };

  // Match patterns like "29th Apr 2026", "1st Jan 2026", "2nd Feb 2026"
  const match = dateStr.trim().match(/^(\d{1,2})(?:st|nd|rd|th)\s+(\w{3})\s+(\d{4})$/);
  if (!match) return dateStr;

  const [, day, monthStr, year] = match;
  const month = months[monthStr];
  if (!month) return dateStr;

  return `${year}-${month}-${day.padStart(2, "0")}`;
}

/**
 * Module-level listener for clearing the "Arrival Date" cart attribute
 * when the cart becomes empty. This runs independently of the arrival-date
 * component lifecycle, so it works even if the component gets removed
 * from the DOM by morphSection before the clear request completes.
 */
let clearListenerAttached = false;

function attachClearListener() {
  if (clearListenerAttached) return;
  clearListenerAttached = true;

  document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
    const detail = /** @type {any} */ (event).detail;

    // Skip events dispatched by the arrival-date component itself
    if (detail?.data?.source === "arrival-date") return;

    // Get item count from the event detail
    const itemCount =
      detail?.data?.itemCount ?? detail?.resource?.item_count;

    // Only act when the cart is empty
    if (itemCount !== 0) return;

    // Clear the "Arrival Date" cart attribute on the server
    try {
      await fetch(
        "/cart/update.js",
        fetchConfig("json", {
          body: JSON.stringify({
            attributes: {
              "Arrival Date": "",
              "Preferred Arrival Date": "",
            },
          }),
        })
      );
    } catch (error) {
      console.error(
        "ArrivalDate: Failed to clear arrival date attribute",
        error
      );
    }
  });
}

// Attach immediately at module load
attachClearListener();

if (!customElements.get("arrival-date")) {
  /**
   * ArrivalDate Component
   * Handles updating cart attributes when the arrival date is changed,
   * validates arrival date before checkout, and shows an error if
   * the date is missing or invalid.
   */
  class ArrivalDate extends Component {
    /** @type {HTMLInputElement | null} */
    #dateInput = null;

    /** @type {HTMLElement | null} */
    #errorContainer = null;

    /** @type {HTMLElement | null} */
    #errorText = null;

    /** @type {AbortController | null} */
    #abortController = null;

    connectedCallback() {
      super.connectedCallback();
      this.#abortController = new AbortController();
      this.#setupElements();
      this.addEventListener("change", this.#onDateChange);

      // Intercept checkout form submission in capture phase
      document.addEventListener("submit", this.#handleFormSubmit, {
        capture: true,
        signal: this.#abortController.signal,
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#abortController?.abort();
      this.#abortController = null;
    }

    /**
     * Setup elements
     */
    #setupElements = () => {
      this.#dateInput = this.querySelector('[data-component="date-input"]');
      this.#errorContainer = this.querySelector(
        '[data-component="arrival-error"]'
      );
      this.#errorText =
        this.#errorContainer?.querySelector(".arrival-date__error-text") || null;

      // After a morph, the server renders the formatted ordinal date
      // (e.g. "29th Apr 2026") into data-arrival-value on the calendar.
      // Parse it back to YYYY-MM-DD so validation works correctly.
      if (this.#dateInput && !this.#dateInput.value) {
        const calendar = this.querySelector("[data-arrival-value]");
        const arrivalValue = calendar?.getAttribute("data-arrival-value") || "";
        if (arrivalValue) {
          const isoDate = parseOrdinalDate(arrivalValue);
          if (isoDate) {
            this.#dateInput.value = isoDate;
          }
        }
      }
    };

    /**
     * Validates the current arrival date value.
     * @returns {{ valid: boolean, message: string }}
     */
    #validate = () => {
      const value = this.#dateInput?.value;

      if (!value || value.trim() === "") {
        return {
          valid: false,
          message: "Please select an arrival date before checking out.",
        };
      }

      // Check if value is a valid date
      const date = new Date(value + "T00:00:00");
      if (isNaN(date.getTime())) {
        return {
          valid: false,
          message: "The selected arrival date is invalid.",
        };
      }

      // Check if the date is not in the past (allow today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return {
          valid: false,
          message: "Arrival date cannot be in the past.",
        };
      }

      return { valid: true, message: "" };
    };

    /**
     * Shows an error message.
     * @param {string} message
     */
    #showError = (message) => {
      if (this.#errorContainer && this.#errorText) {
        this.#errorText.textContent = message;
        this.#errorContainer.classList.remove("hidden");
      }
    };

    /**
     * Hides the error message.
     */
    #hideError = () => {
      if (this.#errorContainer) {
        this.#errorContainer.classList.add("hidden");
      }
    };

    /**
     * Intercepts checkout form submission and validates arrival date.
     * Only blocks if this component has the data-arrival-required attribute.
     * @param {SubmitEvent} event
     */
    #handleFormSubmit = (event) => {
      // Only validate if this component requires it
      if (!this.hasAttribute("data-arrival-required")) return;

      const form = /** @type {HTMLFormElement | null} */ (event.target);
      if (!form || form.id !== "cart-form") return;

      // Only block checkout submissions (not other form actions)
      const submitter = event.submitter;
      if (!submitter || submitter.getAttribute("name") !== "checkout") return;

      const { valid, message } = this.#validate();

      if (!valid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#showError(message);

        // Scroll the error into view
        this.#errorContainer?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        return;
      }

      this.#hideError();
    };

    /**
     * Handles the change event from the nested date input.
     * Updates the "Arrival Date" cart attribute via AJAX and notifies
     * other components to refresh their sections.
     *
     * @param {Event} event - The change event from the input.
     */
    #onDateChange = async (event) => {
      const target = /** @type {HTMLInputElement | null} */ (event.target);
      if (!target || target !== this.#dateInput) return;

      const value = target.value;
      if (value === undefined) return;

      // Hide any existing error when user selects a date
      this.#hideError();

      // Format the date for display in cart attributes
      const formattedDate = formatDateOrdinal(value);

      // Prepare the payload for the cart update
      const body = JSON.stringify({
        attributes: {
          "Arrival Date": formattedDate,
          "Preferred Arrival Date": formattedDate,
        },
      });

      try {
        const response = await fetch(
          "/cart/update.js",
          fetchConfig("json", { body })
        );

        if (!response.ok) {
          throw new Error(
            `Cart update failed: ${response.status} ${response.statusText}`
          );
        }

        const cart = await response.json();

        // Notify other components (like cart drawer) to refresh their sections
        const sectionElement = /** @type {HTMLElement | null} */ (
          this.closest("[data-section-id]")
        );

        document.dispatchEvent(
          new CartUpdateEvent(cart, sectionElement?.dataset.sectionId || "", {
            source: "arrival-date",
          })
        );
      } catch (error) {
        console.error("ArrivalDate: Failed to update cart attribute", error);

        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartError, {
            detail: {
              message: "Failed to save arrival date. Please try again.",
              error: error,
            },
          })
        );
      }
    };

    updatedCallback() {
      super.updatedCallback();
      this.#setupElements();
    }
  }

  customElements.define("arrival-date", ArrivalDate);
}
